import {
  getGenerationCancellationReason,
  isLatestGeneration,
} from '../../../generation-coordinator';
import {
  type GenerationAttempt,
  type GenerationAttemptOutcome,
  type PendingFailureCommit,
} from './attempt-outcome';
import {
  type AutoRetryContext,
  type AutoRetryDecision,
  decideAutoRetry,
} from './auto-retry-policy';

export type AutoRetryTracker = {
  decided: (input: {
    attempt: 1 | 2;
    outcome: GenerationAttemptOutcome;
    decision: AutoRetryDecision;
  }) => void;
};

function stillLatest(outcome: GenerationAttemptOutcome): boolean {
  if (outcome.kind !== 'failed' || !outcome.pendingCommit) return true;
  const { handle } = outcome.pendingCommit;
  return (
    isLatestGeneration(handle) && getGenerationCancellationReason(handle) === undefined
  );
}

export function shouldTrackDecision(
  attempt: 1 | 2,
  outcome: GenerationAttemptOutcome
): boolean {
  return !(
    attempt === 1 &&
    (outcome.kind === 'delegated' || (outcome.kind === 'completed' && !outcome.isEmpty))
  );
}

export async function runWithAutoRetry(deps: {
  attempt: GenerationAttempt;
  readEnv: () => Pick<AutoRetryContext, 'isOnline'>;
  commitFailed: (commit: PendingFailureCommit) => void;
  track: AutoRetryTracker;
}): Promise<GenerationAttemptOutcome> {
  let attempt: 1 | 2 = 1;
  let pendingCommit: PendingFailureCommit | null = null;
  try {
    for (;;) {
      const outcome = await deps.attempt(
        { deferTerminalFailure: true, emptyCloseIsFailure: attempt === 1 },
        attempt
      );
      if (outcome.kind !== 'skipped') {
        pendingCommit = outcome.kind === 'failed' ? outcome.pendingCommit : null;
      }
      const ctx = { attempt, ...deps.readEnv(), isStillLatest: stillLatest(outcome) };
      const decision = decideAutoRetry(outcome, ctx);
      if (shouldTrackDecision(attempt, outcome)) {
        deps.track.decided({ attempt, outcome, decision });
      }
      if (decision.kind === 'retry') {
        attempt = 2;
        continue;
      }
      return outcome;
    }
  } finally {
    // Keep the last deferred failure until the next attempt handles generation.
    // A skipped retry never takes ownership, so the first failure still settles.
    if (pendingCommit) deps.commitFailed(pendingCommit);
  }
}
